import { utf8ToBytes } from "./bytes.js";
import { isValidTotp } from "./totp.js";
export const ITEM_TYPES = ["login", "apiKey", "license", "note"];
export const PAYLOAD_LIMITS = {
    title: 100,
    username: 256,
    password: 4096,
    url: 512,
    notes: 10000,
    totp: 512,
    fields: 30,
    fieldLabel: 64,
    fieldValue: 2000,
    /** Base64url lengths: 1023-byte credential ids and 64-byte user handles (WebAuthn maxima). */
    passkeyCredentialId: 1364,
    passkeyUserHandle: 86,
    passkeyRpId: 253,
    passkeyName: 256,
    passkeyPrivateKey: 512,
    /** Serialized UTF-8 size; keeps the ciphertext under the server's 24 KiB item limit. */
    totalBytes: 20000
};
const TYPE_LABELS = {
    login: "Login",
    apiKey: "API-Schlüssel",
    license: "Softwarelizenz",
    note: "Sichere Notiz"
};
/** What username/password/url mean for each type, so every client labels them the same way. */
const FIELD_LABELS = {
    login: { username: "Benutzername", password: "Passwort", url: "Webadresse" },
    apiKey: { username: "Schlüssel-ID", password: "Secret", url: "Endpunkt / Konsole" },
    license: { username: "Lizenznehmer", password: "Lizenzschlüssel", url: "Hersteller-Website" },
    note: { username: "Benutzername", password: "Geheimnis", url: "Webadresse" }
};
export function fieldLabels(type) {
    return FIELD_LABELS[type];
}
export function isItemType(value) {
    return value === "login" || value === "apiKey" || value === "license" || value === "note";
}
export function itemTypeLabel(type) {
    return TYPE_LABELS[type];
}
/** Only login items are offered for autofill and password capture. */
export function isAutofillable(payload) {
    return payload.type === "login";
}
export function emptyPayload(type = "login") {
    return { type, title: "", username: "", password: "", url: "", notes: "", totp: "", expires: "", fields: [], passkey: null };
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
export function isCustomField(value) {
    return isRecord(value) &&
        typeof value["label"] === "string" &&
        typeof value["value"] === "string" &&
        typeof value["secret"] === "boolean";
}
const PASSKEY_KEYS = ["credentialId", "rpId", "rpName", "userHandle", "userName", "userDisplayName", "privateKey", "createdAt"];
export function isPasskeyCredential(value) {
    return isRecord(value) && PASSKEY_KEYS.every((key) => typeof value[key] === "string");
}
function copyPasskey(p) {
    return {
        credentialId: p.credentialId,
        rpId: p.rpId,
        rpName: p.rpName,
        userHandle: p.userHandle,
        userName: p.userName,
        userDisplayName: p.userDisplayName,
        privateKey: p.privateKey,
        createdAt: p.createdAt
    };
}
/** Strict check for the current shape, e.g. for messages between extension contexts. */
export function isVaultPayload(value) {
    return isRecord(value) &&
        isItemType(value["type"]) &&
        typeof value["title"] === "string" &&
        typeof value["username"] === "string" &&
        typeof value["password"] === "string" &&
        typeof value["url"] === "string" &&
        typeof value["notes"] === "string" &&
        typeof value["totp"] === "string" &&
        typeof value["expires"] === "string" &&
        Array.isArray(value["fields"]) &&
        value["fields"].every(isCustomField) &&
        (value["passkey"] === null || isPasskeyCredential(value["passkey"]));
}
function optionalString(value) {
    if (value === undefined) {
        return "";
    }
    return typeof value === "string" ? value : undefined;
}
/**
 * Accepts v1 and v2 bodies and returns the v2 shape; undefined for anything malformed or an unknown
 * type. A missing or null `passkey` reads as null.
 */
export function normalizePayload(value) {
    if (!isRecord(value)) {
        return undefined;
    }
    const { title, username, password, url, notes } = value;
    if (typeof title !== "string" || typeof username !== "string" || typeof password !== "string" ||
        typeof url !== "string" || typeof notes !== "string") {
        return undefined;
    }
    const rawType = value["type"] ?? "login";
    const totp = optionalString(value["totp"]);
    const expires = optionalString(value["expires"]);
    const rawFields = value["fields"] ?? [];
    if (!isItemType(rawType) || totp === undefined || expires === undefined || !Array.isArray(rawFields)) {
        return undefined;
    }
    const fields = [];
    for (const field of rawFields) {
        if (!isCustomField(field)) {
            return undefined;
        }
        fields.push({ label: field.label, value: field.value, secret: field.secret });
    }
    const rawPasskey = value["passkey"] ?? null;
    if (rawPasskey !== null && !isPasskeyCredential(rawPasskey)) {
        return undefined;
    }
    const passkey = rawPasskey === null ? null : copyPasskey(rawPasskey);
    return { type: rawType, title, username, password, url, notes, totp, expires, fields, passkey };
}
export function parsePayload(json) {
    try {
        return normalizePayload(JSON.parse(json));
    }
    catch {
        return undefined;
    }
}
/**
 * Canonical JSON with a fixed key order; extra keys on the input are dropped. `passkey` is written
 * only when set, so items without one serialize exactly as before it existed.
 */
export function serializePayload(p) {
    const base = {
        type: p.type,
        title: p.title,
        username: p.username,
        password: p.password,
        url: p.url,
        notes: p.notes,
        totp: p.totp,
        expires: p.expires,
        fields: p.fields.map((f) => ({ label: f.label, value: f.value, secret: f.secret }))
    };
    return JSON.stringify(p.passkey === null ? base : { ...base, passkey: copyPasskey(p.passkey) });
}
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function isValidExpiry(value) {
    const m = DATE.exec(value);
    if (m === null) {
        return false;
    }
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}
const BASE64URL = /^[A-Za-z0-9_-]+$/;
const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?Z$/;
const RP_ID = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
function isBase64Url(value, max) {
    return value.length <= max && BASE64URL.test(value) && value.length % 4 !== 1;
}
/** Shape problems of a stored passkey; empty when it is usable. */
export function passkeyProblems(p) {
    const L = PAYLOAD_LIMITS;
    const ok = isBase64Url(p.credentialId, L.passkeyCredentialId) &&
        isBase64Url(p.userHandle, L.passkeyUserHandle) &&
        isBase64Url(p.privateKey, L.passkeyPrivateKey) &&
        p.rpId.length <= L.passkeyRpId && RP_ID.test(p.rpId) &&
        p.rpName.length <= L.passkeyName && p.userName.length <= L.passkeyName && p.userDisplayName.length <= L.passkeyName &&
        ISO_UTC.test(p.createdAt) && !Number.isNaN(Date.parse(p.createdAt));
    return ok ? [] : ["Der Passkey ist beschädigt."];
}
/** German messages for the user; empty when the payload can be saved. */
export function validatePayload(p) {
    const L = PAYLOAD_LIMITS;
    const errors = [];
    if (p.title.trim() === "") {
        errors.push("Titel ist erforderlich.");
    }
    else if (p.title.length > L.title) {
        errors.push(`Titel ist zu lang (max. ${L.title} Zeichen).`);
    }
    if (p.username.length > L.username) {
        errors.push(`Benutzername ist zu lang (max. ${L.username} Zeichen).`);
    }
    if (p.password.length > L.password) {
        errors.push(`Geheimnis ist zu lang (max. ${L.password} Zeichen).`);
    }
    if (p.url.length > L.url) {
        errors.push(`URL ist zu lang (max. ${L.url} Zeichen).`);
    }
    if (p.notes.length > L.notes) {
        errors.push(`Notizen sind zu lang (max. ${L.notes} Zeichen).`);
    }
    if (p.totp.length > L.totp) {
        errors.push("Der 2FA-Schlüssel ist zu lang.");
    }
    else if (p.totp.trim() !== "" && !isValidTotp(p.totp)) {
        errors.push("Der 2FA-Schlüssel ist ungültig (otpauth-Link oder Base32-Geheimnis).");
    }
    if (p.expires !== "" && !isValidExpiry(p.expires)) {
        errors.push("Ablaufdatum ist ungültig.");
    }
    if (p.fields.length > L.fields) {
        errors.push(`Höchstens ${L.fields} eigene Felder.`);
    }
    if (p.fields.some((f) => f.label.trim() === "")) {
        errors.push("Jedes eigene Feld braucht eine Bezeichnung.");
    }
    if (p.fields.some((f) => f.label.length > L.fieldLabel || f.value.length > L.fieldValue)) {
        errors.push(`Eigene Felder: Bezeichnung max. ${L.fieldLabel}, Wert max. ${L.fieldValue} Zeichen.`);
    }
    if (p.passkey !== null) {
        if (p.type !== "login") {
            errors.push("Passkeys gibt es nur bei Logins.");
        }
        else {
            errors.push(...passkeyProblems(p.passkey));
        }
    }
    if (errors.length === 0 && utf8ToBytes(serializePayload(p)).length > L.totalBytes) {
        errors.push("Der Eintrag ist insgesamt zu groß.");
    }
    return errors;
}
/** Days until the expiry date (negative when past); undefined without a valid date. */
export function daysUntilExpiry(p, nowMs) {
    if (p.expires === "" || !isValidExpiry(p.expires)) {
        return undefined;
    }
    const end = Date.parse(`${p.expires}T00:00:00Z`);
    // Calendar days in the user's local time zone.
    const now = new Date(nowMs);
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((end - today) / 86_400_000);
}
//# sourceMappingURL=payload.js.map